import { datadogRum } from '@datadog/browser-rum'
import axios from 'axios'

interface DatadogConfig {
  applicationId: string
  clientToken: string
  site?: string
  service?: string
  env?: string
  version?: string
  sessionSampleRate?: number
  sessionReplaySampleRate?: number
  trackUserInteractions?: boolean
  defaultPrivacyLevel?: 'allow' | 'mask' | 'mask-user-input'
  /** URLs to enable distributed tracing for (connects RUM to APM backend traces) */
  allowedTracingUrls?: string[]
  /** Sample rate for distributed tracing (0-100, default 100) */
  traceSampleRate?: number
}

let isInitialized = false
let configuredSite = 'datadoghq.com'

/**
 * Initialize Datadog RUM
 * Should be called once during application startup
 */
export function initDatadogRum(config: DatadogConfig): void {
  if (isInitialized) {
    console.warn('Datadog RUM is already initialized')
    return
  }

  if (!config.applicationId || !config.clientToken) {
    console.warn(
      'Datadog RUM: Missing required configuration (applicationId or clientToken)',
    )
    return
  }

  try {
    // Build allowedTracingUrls config for RUM-to-APM correlation
    const allowedTracingUrls = config.allowedTracingUrls
      ?.filter(url => url) // Filter out empty strings
      .map(url => ({
        match: url,
        propagatorTypes: ['datadog' as const, 'tracecontext' as const],
      }))

    configuredSite = config.site || 'datadoghq.com'

    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: configuredSite,
      service: config.service || 'ext-app-int-dashboard',
      env: config.env || import.meta.env.MODE || 'development',
      version: config.version || import.meta.env.VITE_APP_VERSION || '0.1.0',
      sessionSampleRate: config.sessionSampleRate ?? 100,
      sessionReplaySampleRate: config.sessionReplaySampleRate ?? 100,
      trackUserInteractions: true,
      defaultPrivacyLevel: config.defaultPrivacyLevel || 'mask-user-input',
      // Enable distributed tracing to connect RUM sessions with APM backend traces
      ...(allowedTracingUrls &&
        allowedTracingUrls.length > 0 && {
          allowedTracingUrls,
          traceSampleRate: config.traceSampleRate ?? 100,
        }),
      beforeSend: () => {
        // Filter out sensitive data if needed
        // Return true to keep the event, false to drop it
        return true
      },
    })

    // Start session replay recording - required for replay to work
    datadogRum.startSessionReplayRecording()

    isInitialized = true
  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error)
  }
}

/**
 * Set user identification for RUM tracking
 * Call this when user authentication state changes
 */
export function setDatadogUser(user: {
  id?: string
  email?: string
  name?: string
  [key: string]: unknown
}): void {
  if (!isInitialized) {
    console.warn('Datadog RUM: Cannot set user - RUM not initialized')
    return
  }

  try {
    datadogRum.setUser({
      id: user.id,
      email: user.email,
      name: user.name,
    })
  } catch (error) {
    console.error('Failed to set Datadog RUM user:', error)
  }
}

/**
 * Clear user identification
 * Call this when user logs out
 */
export function clearDatadogUser(): void {
  if (!isInitialized) {
    return
  }

  try {
    datadogRum.clearUser()
  } catch (error) {
    console.error('Failed to clear Datadog RUM user:', error)
  }
}

/**
 * Generate a DataDog-compatible trace ID.
 * DataDog trace IDs are 64-bit unsigned integers represented as decimal strings.
 */
export function generateTraceId(): string {
  // Generate a 64-bit trace ID using crypto API
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new BigUint64Array(1)
    crypto.getRandomValues(array)
    return array[0].toString()
  }
  // Fallback: use timestamp + random (less entropy but still unique)
  const high = Math.floor(Math.random() * 0xffffffff)
  const low = Math.floor(Math.random() * 0xffffffff)
  return ((BigInt(high) << 32n) | BigInt(low)).toString()
}

/**
 * DataDog tracing headers
 */
export const DATADOG_TRACE_HEADERS = {
  TRACE_ID: 'x-datadog-trace-id',
  PARENT_ID: 'x-datadog-parent-id',
  SAMPLING_PRIORITY: 'x-datadog-sampling-priority',
} as const

/**
 * Create headers with DataDog tracing context.
 * These headers connect frontend requests to backend APM traces.
 *
 * @param existingHeaders - Optional existing headers to merge with
 * @returns Object with trace ID and headers including DataDog tracing
 */
export function createTracedHeaders(existingHeaders?: HeadersInit): {
  traceId: string
  headers: Record<string, string>
} {
  const traceId = generateTraceId()
  const spanId = generateTraceId() // Each request is its own span

  const tracingHeaders: Record<string, string> = {
    [DATADOG_TRACE_HEADERS.TRACE_ID]: traceId,
    [DATADOG_TRACE_HEADERS.PARENT_ID]: spanId,
    [DATADOG_TRACE_HEADERS.SAMPLING_PRIORITY]: '1', // Always sample
  }

  // Merge with existing headers
  const headers: Record<string, string> = { ...tracingHeaders }

  if (existingHeaders) {
    if (existingHeaders instanceof Headers) {
      existingHeaders.forEach((value, key) => {
        headers[key] = value
      })
    } else if (Array.isArray(existingHeaders)) {
      existingHeaders.forEach(([key, value]) => {
        headers[key] = value
      })
    } else {
      Object.assign(headers, existingHeaders)
    }
  }

  return { traceId, headers }
}

/**
 * Get the DataDog app hostname based on configured site.
 * Regional sites (e.g. us3.datadoghq.com) use app.<site> (e.g. app.us3.datadoghq.com).
 */
function getDatadogHostname(): string {
  const site = configuredSite
  if (site === 'datadoghq.eu') {
    return 'app.datadoghq.eu'
  }
  return `app.${site}`
}

/**
 * Generate the DataDog logs search URL for a given trace ID.
 * This searches logs that have been correlated with the trace ID.
 *
 * @param traceId - The DataDog trace ID
 * @returns URL to search logs by trace ID, or null if traceId is empty
 */
export function getDatadogLogsUrl(traceId: string | undefined): string | null {
  if (!traceId) return null

  const hostname = getDatadogHostname()
  // Search for trace_id in logs (DataDog uses @trace_id for the attribute)
  const query = encodeURIComponent(`@trace_id:${traceId}`)

  return `https://${hostname}/logs?query=${query}`
}

/**
 * Install a global Axios request interceptor that injects DataDog trace headers
 * into every outgoing request. This enables automatic trace ID correlation for
 * all services using the default Axios instance.
 *
 * Note: Services that create their own Axios instances (e.g., FCCUtil) need to
 * inject trace headers separately since global interceptors don't apply to them.
 *
 * Call this once during application startup (after DataDog RUM init).
 */
export function setupAxiosTracing(): void {
  axios.interceptors.request.use((config) => {
    const { headers: traceHeaders } = createTracedHeaders()
    if (!config.headers) {
      config.headers = {} as typeof config.headers
    }
    const headers = config.headers
    for (const [key, value] of Object.entries(traceHeaders)) {
      if (key.startsWith('x-datadog-')) {
        if (typeof headers.set === 'function') {
          headers.set(key, value)
        } else {
          ;(headers as Record<string, string>)[key] = value
        }
      }
    }
    return config
  })
}
